import { ChannelTemplate, ChannelTemplateChannel, InvitationEmailChannelTemplateData } from './channel-template.type'
import { InvitationEmailTemplateModel } from './invitation-email-template.model'
import { InvitationEmailTemplateDeclineButtonStyle, InvitationEmailTemplateInfo } from './invitation-email-template.type'

type ChannelTemplateButtonKind = 'confirm' | 'decline'

export class ChannelTemplateModel {
  /**
   * Gets a channel template from a GET `channel-templates` list.
   */
  static find<C extends ChannelTemplateChannel>(templates: ChannelTemplate[] | null | undefined, channel: C): Extract<ChannelTemplate, { channel: C }> | undefined {
    return templates?.find((row): row is Extract<ChannelTemplate, { channel: C }> => row.channel === channel)
  }

  /**
   * Maps API `template_data` into email-template editor `InvitationEmailTemplateInfo`.
   * Pass `overrides.customButtons` only when replacing the full confirm/decline pair;
   * other override keys merge on top of mapped scalar fields without wiping buttons.
   */
  static toInvitationEmailTemplateInfo(
    data: InvitationEmailChannelTemplateData | null | undefined,
    overrides: Partial<InvitationEmailTemplateInfo> = {}
  ): InvitationEmailTemplateInfo {
    const templateData = data ?? {}
    const { customButtons: overrideButtons, ...restOverrides } = overrides

    return InvitationEmailTemplateModel.create({
      subject: templateData.subject,
      message: templateData.invite_message ?? '',
      imageUrl: templateData.image_url,
      image_url: templateData.original_url,
      cardBackgroundColor: templateData.card_background ?? InvitationEmailTemplateModel.DEFAULT_CARD_BACKGROUND,
      calendarButtonColor: templateData.calender_button ?? InvitationEmailTemplateModel.DEFAULT_CALENDER_BUTTON,
      fontFamily: templateData.font_family ?? InvitationEmailTemplateModel.DEFAULT_FONT_FAMILY,
      ...restOverrides,
      customButtons: overrideButtons ?? {
        confirm: ChannelTemplateModel._buttonStyleFromTemplateData(templateData, 'confirm'),
        decline: ChannelTemplateModel._buttonStyleFromTemplateData(templateData, 'decline')
      }
    })
  }

  /**
   * Maps email-template editor `InvitationEmailTemplateInfo` into PATCH `template_data`.
   */
  static toEmailTemplateData(info: InvitationEmailTemplateInfo): InvitationEmailChannelTemplateData {
    const confirm = info.customButtons?.confirm ?? InvitationEmailTemplateModel.DEFAULT_BUTTONS.confirm
    const decline = info.customButtons?.decline ?? InvitationEmailTemplateModel.DEFAULT_BUTTONS.decline

    return {
      subject: info.subject,
      invite_message: info.message,
      calender_button: info.calendarButtonColor ?? InvitationEmailTemplateModel.DEFAULT_CALENDER_BUTTON,
      card_background: info.cardBackgroundColor ?? InvitationEmailTemplateModel.DEFAULT_CARD_BACKGROUND,
      confirm_background_color: confirm.backgroundColor,
      confirm_font_size: InvitationEmailTemplateModel.toFontSizeCss(confirm.fontSize),
      confirm_invitation_text: confirm.text,
      confirm_is_bold: !!confirm.isBold,
      confirm_is_italic: !!confirm.isItalic,
      confirm_is_underline: !!confirm.isUnderline,
      confirm_text_color: confirm.textColor,
      decline_background_color: decline.backgroundColor,
      decline_font_size: InvitationEmailTemplateModel.toFontSizeCss(decline.fontSize),
      decline_invitation_text: decline.hidden ? '' : decline.text,
      decline_is_bold: !!decline.isBold,
      decline_is_italic: !!decline.isItalic,
      decline_is_underline: !!decline.isUnderline,
      decline_text_color: decline.textColor,
      font_family: info.fontFamily ?? InvitationEmailTemplateModel.DEFAULT_FONT_FAMILY,
      image_url: info.imageUrl,
      original_url: info.image_url
    }
  }

  /**
   * Converts channel template data to email-template button style.
   */
  private static _buttonStyleFromTemplateData(data: InvitationEmailChannelTemplateData, kind: ChannelTemplateButtonKind): InvitationEmailTemplateDeclineButtonStyle {
    const defaults = InvitationEmailTemplateModel.DEFAULT_BUTTONS[kind]

    if (kind === 'confirm') {
      return {
        text: data.confirm_invitation_text ?? defaults.text,
        textColor: data.confirm_text_color ?? defaults.textColor,
        backgroundColor: data.confirm_background_color ?? defaults.backgroundColor,
        isBold: data.confirm_is_bold ?? false,
        isItalic: data.confirm_is_italic ?? false,
        isUnderline: data.confirm_is_underline ?? false,
        fontSize: InvitationEmailTemplateModel.toFontSizeCss(data.confirm_font_size)
      }
    }

    const hidden = data.decline_invitation_text === ''

    return {
      text: hidden ? defaults.text : data.decline_invitation_text ?? defaults.text,
      textColor: data.decline_text_color ?? defaults.textColor,
      backgroundColor: data.decline_background_color ?? defaults.backgroundColor,
      isBold: data.decline_is_bold ?? false,
      isItalic: data.decline_is_italic ?? false,
      isUnderline: data.decline_is_underline ?? false,
      fontSize: InvitationEmailTemplateModel.toFontSizeCss(data.decline_font_size),
      hidden
    }
  }
}
